<template>
  <div class="schedule-entrance-test-dialog-root">
    <el-dialog
      v-if="!embedded"
      class="schedule-entrance-test-dialog"
      :visible="visible"
      width="700px"
      :show-close="false"
      :close-on-click-modal="false"
      append-to-body
      @close="handleClose"
    >
      <div
        slot="title"
        class="schedule-entrance-test-dialog__header d-flex align-items-center justify-content-between px-4 py-3"
      >
        <h3 class="font-weight-bold mb-0">{{ title }}</h3>
        <button
          type="button"
          class="schedule-entrance-test-dialog__close btn p-0 d-inline-flex align-items-center justify-content-center"
          :disabled="confirmLoading"
          @click="handleClose"
        >
          <i class="el-icon-close"></i>
        </button>
      </div>

      <div class="schedule-entrance-test-dialog__body bg-white px-4 pt-4 pb-2">
        <component :is="stepComponent" v-bind="stepBindings" v-on="stepListeners" :ref="stepRefName" />
      </div>

      <div slot="footer" class="schedule-entrance-test-dialog__footer d-flex justify-content-end">
        <el-button
          class="schedule-entrance-test-dialog__cancel"
          :disabled="confirmLoading"
          @click="handleSecondaryClick"
        >
          {{ secondaryButtonText }}
        </el-button>
        <el-button
          v-if="step !== STEP_CENTER"
          class="schedule-entrance-test-dialog__confirm"
          type="primary"
          :loading="confirmLoading"
          :disabled="confirmLoading"
          @click="handlePrimaryClick"
        >
          Xác nhận
        </el-button>
      </div>
    </el-dialog>

    <div v-else class="schedule-entrance-test-dialog schedule-entrance-test-dialog--embedded">
      <div class="schedule-entrance-test-dialog__body bg-white px-0 pt-0 pb-2">
        <component :is="stepComponent" v-bind="stepBindings" v-on="stepListeners" :ref="stepRefName" />
      </div>
      <div class="d-flex justify-content-end mt-3">
        <el-button class="schedule-entrance-test-dialog__cancel" @click="handleSecondaryClick">
          {{ secondaryButtonText }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { CENTER_LIST } from "@/core/services/store/customer/customer.module";
import { TOEIC } from "@/core/config/center/centerOption";
import { Mentor_English_Mastery_ID } from "@/core/config/testInput/testInputOption";
import { isMasteryCenter } from "@/core/config/testInput/scheduleFieldOption";
import ScheduleCenterStep from "./entrance/components/ScheduleCenterStep.vue";
import SchedulePredictedLevelStep, {
  PREDICTED_LEVEL_BASIC,
  PREDICTED_LEVEL_HIGHER
} from "./entrance/components/SchedulePredictedLevelStep.vue";
import MasteryScheduleForm from "./entrance/components/MasteryScheduleForm.vue";

const STEP_CENTER = "center";
const STEP_PREDICTED = "predicted";
const STEP_MASTERY = "mastery";

export default {
  name: "ScheduleEntranceTestDialog",
  components: {
    ScheduleCenterStep,
    SchedulePredictedLevelStep,
    MasteryScheduleForm
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    isCreateCustomer: {
      type: Boolean,
      default: false
    },
    embedded: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: "Đặt lịch test đầu vào"
    },
    // Có giá trị = chế độ đăng ký thi lại: khoá trung tâm và luôn là thi đầu khóa
    retakeCenterId: {
      default: null
    },
    confirmLoading: {
      type: Boolean,
      default: false
    },
    closeOnConfirm: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      step: STEP_CENTER,
      STEP_CENTER,
      STEP_PREDICTED,
      STEP_MASTERY,
      centers: [],
      loadingCenters: false,
      centerId: null,
      predictedLevel: null,
      examState: {
        marking_exam_shift_id: null,
        has_error: false,
        error_message: "",
        is_complete: false
      },
      masteryPayload: null
    };
  },
  computed: {
    isMastery() {
      return isMasteryCenter(this.centerId, Mentor_English_Mastery_ID);
    },
    isRetakeMode() {
      return this.retakeCenterId !== null && this.retakeCenterId !== undefined && this.retakeCenterId !== "";
    },
    stepComponent() {
      if (this.step === STEP_MASTERY) return "MasteryScheduleForm";
      if (this.step === STEP_PREDICTED) return "SchedulePredictedLevelStep";
      return "ScheduleCenterStep";
    },
    stepRefName() {
      if (this.step === STEP_MASTERY) return "masteryForm";
      if (this.step === STEP_PREDICTED) return "predictedStep";
      return "centerStep";
    },
    stepBindings() {
      if (this.step === STEP_MASTERY) {
        return {
          centerId: this.centerId,
          centers: this.centers
        };
      }
      if (this.step === STEP_PREDICTED) {
        return {
          centers: this.centers,
          centerId: this.centerId,
          predictedLevel: this.predictedLevel,
          disabledCenter: this.isRetakeMode,
          disabledPredictedLevel: this.isRetakeMode
        };
      }
      return {
        centers: this.centers,
        centerId: this.centerId,
        loadingCenters: this.loadingCenters
      };
    },
    stepListeners() {
      if (this.step === STEP_MASTERY) {
        return {
          infoTestInput: (payload) => {
            this.masteryPayload = payload;
            this.emitScheduleChange();
          },
          "update:centerId": (val) => {
            this.handleCenterChange(val);
          }
        };
      }
      if (this.step === STEP_PREDICTED) {
        return {
          "update:centerId": (val) => {
            this.handleCenterChange(val);
          },
          "update:predictedLevel": (val) => {
            this.predictedLevel = val;
            if (val !== PREDICTED_LEVEL_HIGHER) {
              this.resetExamState();
            }
            this.emitScheduleChange();
          },
          "exam-state-change": (state) => {
            this.examState = { ...this.examState, ...state };
            this.emitScheduleChange();
          }
        };
      }
      return {
        "update:centerId": (val) => {
          this.handleCenterChange(val);
        }
      };
    },
    secondaryButtonText() {
      return "Hủy bỏ";
    }
  },
  watch: {
    visible(val) {
      if (val || this.embedded) {
        this.resetDialog();
        this.getCenters();
      }
    }
  },
  mounted() {
    if (this.embedded || this.visible) {
      this.resetDialog();
      this.getCenters();
    }
  },
  methods: {
    resetExamState() {
      this.examState = {
        marking_exam_shift_id: null,
        has_error: false,
        error_message: "",
        is_complete: false
      };
    },
    resetDialog() {
      this.masteryPayload = null;
      this.resetExamState();
      if (this.isRetakeMode) {
        this.step = STEP_PREDICTED;
        this.centerId = Number(this.retakeCenterId);
        this.predictedLevel = PREDICTED_LEVEL_HIGHER;
      } else {
        this.step = STEP_CENTER;
        this.centerId = null;
        this.predictedLevel = null;
      }
      if (this.isCreateCustomer || this.embedded) {
        this.emitScheduleChange();
      }
    },
    isMasteryPayloadComplete(payload) {
      if (!payload) return false;
      const gradeMissing =
        payload.grade_level === "" ||
        payload.grade_level === null ||
        payload.grade_level === undefined;
      return Boolean(
        payload.center_id &&
          payload.branch_id &&
          payload.hinh_thuc_test_dau_vao &&
          !gradeMissing &&
          payload.ngay_hen &&
          payload.time
      );
    },
    emitScheduleChange() {
      if (!(this.isCreateCustomer || this.embedded)) {
        return;
      }

      if (!this.centerId) {
        this.$emit("schedule-change", { type: null });
        return;
      }

      if (this.step === STEP_MASTERY) {
        if (this.isMasteryPayloadComplete(this.masteryPayload)) {
          this.$emit("schedule-change", {
            type: "mastery",
            center_id: this.centerId,
            testInput: this.masteryPayload
          });
          return;
        }
        this.$emit("schedule-change", { type: null });
        return;
      }

      if (this.step === STEP_PREDICTED) {
        if (this.predictedLevel === PREDICTED_LEVEL_BASIC) {
          this.$emit("schedule-change", {
            type: "basic",
            center_id: this.centerId
          });
          return;
        }
        if (
          this.predictedLevel === PREDICTED_LEVEL_HIGHER &&
          this.examState.is_complete &&
          !this.examState.has_error &&
          this.examState.marking_exam_shift_id
        ) {
          this.$emit("schedule-change", {
            type: "higher",
            center_id: this.centerId,
            marking_exam_shift_id: this.examState.marking_exam_shift_id
          });
          return;
        }
      }

      this.$emit("schedule-change", { type: null });
    },
    handleCenterChange(val) {
      this.centerId = val || null;
      this.predictedLevel = null;
      this.masteryPayload = null;
      this.resetExamState();

      if (!val) {
        this.step = STEP_CENTER;
        this.emitScheduleChange();
        return;
      }

      if (isMasteryCenter(val, Mentor_English_Mastery_ID)) {
        this.step = STEP_MASTERY;
        this.emitScheduleChange();
        return;
      }

      this.step = STEP_PREDICTED;
      this.emitScheduleChange();
    },
    getCenters() {
      this.loadingCenters = true;
      this.$store
        .dispatch(CENTER_LIST)
        .then((response) => {
          if (response.status === 422) {
            this.$notify({
              title: "Warning",
              message: `Serve đang bận, vui lòng thử lại sau: ${response.data.message}`,
              type: "warning"
            });
            return;
          }
          this.centers = response.data.filter(
            (center) => center.is_edutalk === 1 && center.id !== TOEIC
          );
        })
        .finally(() => {
          this.loadingCenters = false;
        });
    },
    handleClose() {
      if (this.confirmLoading) {
        return;
      }
      this.$emit("update:visible", false);
      this.resetDialog();
    },
    handleSecondaryClick() {
      if (this.confirmLoading) {
        return;
      }
      if (this.embedded) {
        this.resetDialog();
        this.$emit("cancel");
        return;
      }
      this.handleClose();
    },
    async handlePrimaryClick() {
      if (this.confirmLoading) {
        return;
      }
      if (this.step === STEP_CENTER) {
        this.$message({ type: "warning", message: "Vui lòng chọn trung tâm", showClose: true });
        return;
      }

      if (this.step === STEP_PREDICTED) {
        await this.submitPredictedStep();
        return;
      }

      if (this.step === STEP_MASTERY) {
        await this.submitMasteryStep();
      }
    },
    async submitPredictedStep() {
      if (!this.predictedLevel) {
        this.$message({ type: "warning", message: "Vui lòng chọn dự đoán trình độ", showClose: true });
        return;
      }

      if (this.predictedLevel === PREDICTED_LEVEL_BASIC) {
        this.emitConfirm({ type: "basic", center_id: this.centerId });
        return;
      }

      await this.$nextTick();
      const valid = await this.$refs.predictedStep?.validateExam();
      if (!valid || this.examState.has_error || !this.examState.marking_exam_shift_id) {
        this.$message({
          type: "warning",
          message: this.examState.error_message || "Vui lòng chọn ngày thi và giờ thi",
          showClose: true
        });
        return;
      }

      this.emitConfirm({
        type: "higher",
        center_id: this.centerId,
        marking_exam_shift_id: Number(this.examState.marking_exam_shift_id)
      });
    },
    emitConfirm(payload) {
      this.$emit("confirm", payload);
      if (this.closeOnConfirm) {
        this.handleClose();
      }
    },
    async submitMasteryStep() {
      await this.$nextTick();
      const valid = await this.$refs.masteryForm?.validateForm();
      if (!valid) {
        return;
      }
      const payload = this.$refs.masteryForm?.getPayload() || this.masteryPayload;
      if (!payload) {
        return;
      }

      this.emitConfirm({
        type: "mastery",
        center_id: this.centerId,
        testInput: payload
      });
    }
  }
};
</script>

<style src="./entrance/entrance-dialog.css"></style>
